import { useMelwaysInfo } from '@seek/melways-react';
import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Stack,
  Heading,
  TabsProvider,
  Tabs,
  Tab,
  TabPanels,
  TabPanel,
  Box,
  IconProfile,
  IconNotification,
  IconVisibility,
} from 'braid-design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Account } from '../Account/Account';
import { Notifications } from '../Notifications/Notifications';
import { useRoutes } from '../Notifications/routes';
import UnsubscribeContainer from '../Notifications/unsubscribe/container';
import { Visibility } from '../Visibility/Visibility';

import translations from './.vocab';

import * as styles from './Settings.css';

const languagePathVariable = ':language?';

export const tabRoutes = [
  {
    id: 'account',
    path: `${languagePathVariable}/settings`,
    icon: <IconProfile />,
    component: <Account />,
  },
  {
    id: 'visibility',
    path: `${languagePathVariable}/settings/visibility`,
    icon: <IconVisibility />,
    component: <Visibility />,
  },
  {
    id: 'notifications',
    path: `${languagePathVariable}/settings/notifications`,
    icon: <IconNotification />,
    component: <Notifications />,
  },
];

const languagePathVarWithLanguage = (path: string, language: Language) =>
  language !== 'en'
    ? path.replace(`${languagePathVariable}`, `/${language}`)
    : path.replace(`${languagePathVariable}`, '');

const getPathFromId = (id: string, language: Language) => {
  const path = tabRoutes.find((route) => route.id === id)?.path || '';
  return languagePathVarWithLanguage(path, language);
};

export const Settings = () => {
  const { t } = useTranslations(translations);
  const { language } = useMelwaysInfo();

  const navigate = useNavigate();

  const getTabData = useCallback(
    (tabId: string) => {
      switch (tabId) {
        case 'account':
          return {
            tabTitle: t('Account'),
          };
        case 'visibility':
          return {
            tabTitle: t('Visibility'),
          };
        case 'notifications':
          return {
            tabTitle: t('Notifications'),
          };
      }
    },
    [t],
  );

  const routesForTabs = useMemo(
    () =>
      tabRoutes.map((tab) => ({
        ...tab,
        ...getTabData(tab.id),
      })),
    [getTabData],
  );

  const { pathname } = useLocation();

  const { unsubscribePath } = useRoutes();

  const activeTabFromPath =
    routesForTabs.find(
      (route) => languagePathVarWithLanguage(route.path, language) === pathname,
    )?.id || tabRoutes[0].id;

  const [activeTab, setActiveTab] = useState(activeTabFromPath);

  useEffect(() => {
    setActiveTab(activeTabFromPath);
  }, [activeTabFromPath]);

  if (pathname === unsubscribePath) {
    return <UnsubscribeContainer />;
  }

  return (
    <Stack space="xlarge">
      <Heading level="2" component="h1">
        {t('Settings')}
      </Heading>
      <TabsProvider
        id="navigation"
        selectedItem={activeTab}
        onChange={(_index, tab = tabRoutes[0].id) => {
          setActiveTab(tab);
          return navigate(getPathFromId(tab, language));
        }}
      >
        <Stack space="large">
          <Tabs label="Navigation">
            {routesForTabs.map(({ tabTitle, icon, id }) => (
              <Tab
                item={id}
                key={id}
                icon={icon}
                data={{ testid: `${id}-tab` }}
              >
                <Box className={styles.largeHitArea}>{tabTitle}</Box>
              </Tab>
            ))}
          </Tabs>
          <TabPanels>
            {routesForTabs.map(({ id, component }) => (
              <TabPanel key={id} item={id}>
                {component}
              </TabPanel>
            ))}
          </TabPanels>
        </Stack>
      </TabsProvider>
    </Stack>
  );
};
